import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Role } from "../types";

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Permesso nominato richiesto. È il gate preferito: stessa fonte del menu e del
   * backend (permessi effettivi, override inclusi), così le tre liste non
   * divergono più. `allowedRoles` resta per le poche route senza un singolo
   * permesso (es. il Calendario unificato).
   */
  permission?: string;
  /** Se omesso (e senza `permission`), la route è accessibile a qualunque utente autenticato. */
  allowedRoles?: Role[];
}

export function ProtectedRoute({ children, permission, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Evita un lampo di redirect verso /login mentre verifichiamo il token salvato.
    return (
      <div className="flex h-screen items-center justify-center text-sm text-ink/50">
        Caricamento…
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Password temporanea da cambiare: si va solo alla pagina del cambio. Il
  // vincolo vero è nel backend (403 su tutte le API tranne me e
  // change-password); questo redirect evita di mostrare pagine che non
  // potrebbero caricare nulla.
  if (user.must_change_password && location.pathname !== "/cambio-password") {
    return <Navigate to="/cambio-password" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Inverso di ProtectedRoute: usato per /login e /register, che non hanno
 * senso per un utente già autenticato (viene rimandato alla dashboard).
 */
export function PublicRoute({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  // Con la password temporanea la destinazione è una sola, e la si dice qui:
  // passare da "/" affidava il secondo salto a ProtectedRoute, e per un istante
  // la destinazione normale restava "/" mentre `Login` navigava altrove.
  if (isAuthenticated && user?.must_change_password) return <Navigate to="/cambio-password" replace />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <>{children}</>;
}
